// ─────────────────────────────────────────────────────────────
//  Research Report – Datenstruktur
//
//  Diese Datei beschreibt EINEN Report. Später wird daraus eine
//  dynamische Quelle (z.B. Firebase / API), die pro Unternehmen
//  ein solches Objekt liefert. Die ResearchPage rendert rein
//  datengetrieben – es muss also nur dieses Objekt ausgetauscht
//  werden, um eine neue Research-Seite zu erzeugen.
//
//  Alle Geld-Zahlen (Preise, Szenarien, Fee vs. Upside) werden NICHT
//  hier hartkodiert, sondern zur Laufzeit aus `config` über die
//  pricingEngine berechnet. Dadurch kann im Sales-Call lokal an den
//  Stellschrauben gedreht werden (Deal-Wert, Marge, Paket, ...), ohne
//  dass etwas gespeichert wird.
//
//  Wichtig: Das System ist bewusst ehrlich gehalten. Der Score
//  spiegelt das echte Outbound-/Abschluss-Potential wider und
//  darf auch niedrig ausfallen, wenn ein Case schwach ist.
// ─────────────────────────────────────────────────────────────

export const sampleReport = {
  // ── Meta des Reports ──
  product: 'SalesGod',
  reportType: 'Opportunity Analysis',
  confidential: true,

  // ── Unternehmen ──
  company: {
    name: 'SecuraNova Sicherheitstechnik GmbH',
    industry: 'Sicherheitstechnik, Alarmanlagen, Zutrittskontrolle & Videoüberwachung',
    audience:
      'Gewerbebetriebe, Hausverwaltungen, Lagerflächen, Arztpraxen, Kanzleien, Hotels und mittelständische Unternehmen',
  },

  // ── Live-Config (treibt alle Geld-Zahlen über die pricingEngine) ──
  //    Diese Werte sind im Browser lokal anpassbar und werden nicht gespeichert.
  config: {
    revenuePerClose: 15000, // Ø Kundenumsatz pro Abschluss (€)
    profitMargin: 0.35, // geschätzte Marge
    tier: 1, // 0 = Foundation, 1 = Plus, 2 = Premium
    valueBasedPricingEnabled: true,
    quality: {
      uniquenessScore: 8, // 0–10
      marketNeedScore: 8, // 0–10
      blueOceanScore: 8, // 0–10
    },
    expectedApptsOverride: null, // null → Tier-Default (6/12/24)
    setup: 0,
    // Abschlüsse pro Termin: treibt AUSSCHLIESSLICH den ROI / die Neukunden-Szenarien.
    //   Neukunden/Jahr = Termine/Monat × 12 × closeRate (konservativ/stark über SCENARIO_SPREAD).
    //   Der Termin-Preis ist davon entkoppelt (feste Referenz-Quote im pricingEngine).
    closeRate: 0.30,
    // Termin-Preis ab dem 4. Termin: 'auto' (aus Deal-Wert) oder 'manual' (Slider).
    apptValueMode: 'auto',
    apptValueManual: 250, // greift nur bei apptValueMode === 'manual'
    // Monatlicher Retainer: 'auto' (Paketpreis × Multiplikator) oder 'manual' (Slider).
    monthlyPriceMode: 'auto',
    monthlyPriceManual: 2860, // greift nur bei monthlyPriceMode === 'manual'
  },

  // ── Gesamt-Verdict (Label & Score werden dynamisch berechnet) ──
  verdict: {
    summary: [
      'SecuraNova ist ein starker Outbound-Case, weil das Angebot einen klaren wirtschaftlichen und emotionalen Pain adressiert: Sicherheit, Risiko, Einbruchschutz, Zutrittskontrolle und Schutz von Eigentum.',
      'Der Markt ist gut erreichbar, die Zielgruppe ist klar definierbar – und je höher der wirtschaftliche Hebel pro Kunde, desto besser der Fit.',
    ],
    leverage: {
      title: 'Wo der größte Hebel liegt',
      negative: 'Der größte Hebel liegt nicht darin, möglichst viele Termine zu erzeugen.',
      positive:
        'Der größte Hebel liegt darin, gezielt jene Unternehmen zu erreichen, bei denen Sicherheit gerade ein echtes Risiko- oder Modernisierungsthema ist.',
    },
  },

  // ── Statische Research-Dimensionen (KI-Bonus, unabhängig von der Live-Ökonomie) ──
  //    Diese Punkte kommen aus der Recherche und bilden den qualitativen Teil des
  //    Fit-Scores. Der wirtschaftliche Teil (ROI-Fit + Kundenwert) wird zur Laufzeit
  //    aus der config berechnet und dazu addiert.
  researchScores: [
    {
      label: 'Cold Outreach Potential',
      score: 8,
      max: 10,
      note: 'Zielgruppe öffentlich auffindbar, Entscheider klar identifizierbar.',
    },
    {
      label: 'Markterreichbarkeit',
      score: 7,
      max: 10,
      note: 'Segmente gut recherchierbar, aber breit gestreut.',
    },
    {
      label: 'Abschlussfähigkeit / Sales Cycle',
      score: 6,
      max: 10,
      note: 'Erklärungsbedürftig, mehrere Gespräche bis zum Abschluss möglich.',
    },
  ],

  // ── Wirtschaftliche Szenarien (Zahlen werden live berechnet) ──
  scenarios: {
    note: 'Der Base Case setzt nicht voraus, dass jeder Termin sofort abschließt. Er setzt voraus, dass aus einem sauberen Outbound-System über zwölf Monate hinweg regelmäßig qualifizierte Chancen entstehen.',
  },

  // ── Best Case vs. Worst Case Framing ──
  riskFraming: {
    worstCaseMonths: 3, // Worst Case = X Monate Fixfee ohne Abschluss
    worstCaseTitle: '3 Monate Fee, kein einziger Abschluss',
    worstCaseSub: 'Reine Fixfee im Testzeitraum – ohne jeden gewonnenen Neukunden.',
    worstCaseStill: 'Und selbst in diesem Fall bekommst du:',
    worstCaseInsights: [
      'Validierte Zielgruppen- & ICP-Daten',
      'Getestete Pain-Angles & Messaging',
      'Qualifizierte Markt- & Gesprächs-Insights',
      'Vollständiges Outbound-Reporting',
    ],
    bestCaseSub: 'Profit-Upside im starken Szenario.',
    bestCaseStill: 'Zusätzlich entsteht:',
    bestCaseExtras: [
      'Planbarer, wiederholbarer Akquise-Kanal',
      'Skalierbar über die Folgejahre',
      'Folgegeschäft über die gesamte Kundenbeziehung',
    ],
    note: 'Der Kern des Cases ist die Asymmetrie: Das Risiko ist auf wenige Monate Fixfee begrenzt – das Upside ist ein Vielfaches davon.',
  },

  // ── Fee vs. Upside (Zahlen werden live berechnet) ──
  feeVsUpside: {
    intro: 'Wie steht die Investition im Verhältnis zum möglichen Gewinn?',
    interpretation: [
      'Der Wert entsteht nicht durch günstige Leadgenerierung, sondern durch planbare Gespräche mit Unternehmen, bei denen ein Sicherheitsprojekt wirtschaftlich relevant ist.',
      'Wirklich attraktiv wird der Case, wenn ein Teil der Kunden langfristig betreut wird, Wartungsverträge entstehen oder Folgeumsätze durch Erweiterungen, Service und Modernisierung dazukommen.',
    ],
    valueNote:
      'Der eigentliche Hebel liegt also nicht nur im Erstabschluss. Er liegt im Kundenwert über die gesamte Beziehung.',
  },

  // ── Executive Summary ──
  executiveSummary: {
    paragraphs: [
      'SecuraNova ist ein sehr guter SalesGod-Kunde, weil Markt, Kundenwert und Zielgruppenlogik grundsätzlich zusammenpassen.',
      'Der Case ist nicht perfekt, weil Sicherheitstechnik erklärungsbedürftig ist und der Sales Cycle länger sein kann. Aber genau deshalb ist eine strukturierte Outbound-Testphase sinnvoll.',
    ],
    bestSegmentsTitle: 'Die größten Chancen liegen in Zielgruppen mit hohem Sicherheitsbedarf und klarer Entscheidungsstruktur:',
    bestSegments: [
      'Hausverwaltungen',
      'Gewerbeimmobilien',
      'Lagerbetriebe',
      'Hotels',
      'Arztpraxen',
      'Kanzleien',
      'Mittelständische Unternehmen mit mehreren Standorten',
    ],
    closing:
      'Nicht über Masse. Sondern über präzise Zielgruppen, klare Pain-Angles und wirtschaftlich relevante Gespräche.',
  },

  // ── RESEARCH-TAB ──
  research: {
    coldOutreach: {
      score: 17,
      max: 20,
      headline: 'Das Cold-Outreach-Potential ist hoch.',
      body: [
        'SecuraNova verkauft kein abstraktes Beratungsprodukt, sondern eine konkrete Lösung für ein reales Problem. Unternehmen verstehen schnell, warum Alarmanlagen, Zutrittskontrolle und Videoüberwachung relevant sein können.',
        'Besonders stark ist der Case, weil die Zielgruppen öffentlich gut auffindbar sind und die Entscheider meist klar identifizierbar sind: Geschäftsführer, Facility Manager, Immobilienverwalter, Office Manager oder technische Leiter.',
      ],
      assessment:
        'Cold Outreach kann hier funktionieren, wenn die Ansprache nicht generisch über „Sicherheitstechnik“ läuft, sondern über konkrete Risikosituationen und messbare Anlässe.',
    },
    targetGroups: [
      'Hausverwaltungen',
      'Gewerbeparks',
      'Hotels',
      'Arztpraxen',
      'Kanzleien',
      'Lager- und Logistikbetriebe',
      'Produktionsbetriebe',
      'Einzelhandelsketten',
      'Bürogebäude mit mehreren Mietern',
    ],

    // ── Zielgruppen-Deep-Dive (Recherche-Output, im Tool editierbar) ──
    //    Bewusst knapp gehalten: Schlagworte statt Fließtext. Items können im
    //    Research-Tab aus-/abgewählt, ergänzt und entfernt werden.
    targetResearch: {
      ideas: [
        'Hausverwaltungen & WEG-Verwalter',
        'Gewerbe- & Logistikparks',
        'Hotellerie (3–5 Sterne & Ketten)',
        'Arztpraxen, MVZ & Kliniken',
        'Kanzleien & Steuerberater',
        'Produktions- & Industriebetriebe',
        'Einzelhandelsfilialisten',
        'Rechenzentren & Telekommunikation',
      ],
      keywords: [],
      hashtags: [],
      channels: [
        'E-Mail (Cold Outreach)',
        'LinkedIn',
        'XING',
        'Verbandsverzeichnisse',
        'Google Maps & Branchenportale',
        'Messen & Fachevents',
      ],
      decisionMakers: [
        'Geschäftsführer / Inhaber',
        'Facility Manager',
        'Immobilien- & Objektverwalter',
        'Sicherheitsbeauftragte',
        'Kaufm. Leitung / Prokurist',
        'Office Manager',
      ],
      regions: [],
      revenueBands: [
        '< 2 Mio. €',
        '2–10 Mio. €',
        '10–50 Mio. €',
        '50–250 Mio. €',
      ],
    },
    channelStrategy: {
      intro: 'Am stärksten ist die direkte, gezielte Ansprache. Soziale Netzwerke spielen für diese Zielgruppe nur eine Nebenrolle.',
      channels: [
        { name: 'E-Mail (Cold Outreach)', fit: 90, recommendation: 'Direkter Draht zu Geschäftsführung und Facility Management. Ideal für konkrete, anlassbezogene Ansprache.' },
        { name: 'Google Suche & SEO', fit: 85, recommendation: 'Entscheider recherchieren aktiv nach Sicherheitslösungen. Suchanzeigen und gute Sichtbarkeit greifen genau im Bedarfsmoment.' },
        { name: 'LinkedIn', fit: 70, recommendation: 'Gut für die Ansprache von Entscheidern in mittelständischen und größeren Betrieben.' },
        { name: 'XING & Branchenportale', fit: 55, recommendation: 'Im DACH-Mittelstand weiterhin nützlich, vor allem für regionale Betriebe und Verbände.' },
        { name: 'Facebook', fit: 35, recommendation: 'Eher für kleinere lokale Betriebe relevant, kaum für gezielte B2B-Entscheideransprache.' },
        { name: 'Instagram', fit: 25, recommendation: 'Visuell, aber kaum Such- oder Kaufabsicht für gewerbliche Sicherheitstechnik.' },
        { name: 'YouTube', fit: 30, recommendation: 'Sinnvoll für Vertrauen und Referenzen, jedoch selten als aktiver Suchkanal für dieses Thema.' },
        { name: 'TikTok', fit: 10, recommendation: 'Die gewerblichen Entscheider sind hier kaum erreichbar. Für dieses Angebot nicht sinnvoll.' },
      ],
    },
    angles: [
      {
        title: 'Sicherheitslücken in Gewerbeobjekten',
        body: 'Viele Unternehmen investieren erst nach einem Vorfall in Sicherheit. Der bessere Angle ist: Risiken erkennen, bevor sie teuer werden.',
      },
      {
        title: 'Modernisierung alter Alarm- und Zutrittssysteme',
        body: 'Viele Betriebe haben Systeme, die technisch veraltet sind, schlecht gewartet werden oder nicht mehr zum heutigen Betrieb passen.',
      },
      {
        title: 'Schutz von Standorten mit hohem Wert',
        body: 'Bei Praxen, Kanzleien, Lagern und Hotels geht es nicht nur um Einbruchschutz, sondern um Betriebssicherheit und Verantwortung.',
      },
    ],
    offerAnalysis: {
      whatIsSold: 'Verkauf, Installation und Wartung von Alarmanlagen, Zutrittskontrolle und Videoüberwachung für Gewerbeobjekte, als Komplettlösung aus einer Hand.',
      usp: {
        text: 'Die Differenzierung liegt in der durchgehenden Betreuung mit eigenen Teams.',
        points: [
          'Eigene Technik-Teams statt Subunternehmer',
          'Reaktionszeit unter 24 Stunden',
          'Komplettbetreuung über die gesamte Anlagenlaufzeit',
        ],
      },
      salesSetup: {
        have: [
          'Vertriebsteam mit 2 bis 4 Personen',
          'Bestandskunden und Empfehlungen als Hauptquelle',
        ],
        missing: [
          'Kein aktives Outbound',
          'Keine systematische Lead-Nachverfolgung',
          'Kein CRM im Einsatz',
        ],
      },
      noBrainerOffer: 'Kostenloser Sicherheits-Check vor Ort inklusive schriftlichem Schwachstellen-Bericht.',
    },
    whyItWorks: [
      {
        title: 'Der Pain ist real.',
        body: 'Sicherheitsrisiken sind für Unternehmen kein Luxusproblem. Wenn etwas passiert, wird es teuer, unangenehm und oft reputationsschädigend.',
      },
      {
        title: 'Die Zielgruppe ist auffindbar.',
        body: 'Gewerbebetriebe, Immobilienverwalter, Hotels, Praxen und Lagerflächen lassen sich strukturiert recherchieren und segmentieren.',
      },
      {
        title: 'Der Kundenwert ist hoch genug.',
        body: 'Schon bei moderaten Abschlusszahlen wird Outbound bei diesem Deal-Wert wirtschaftlich interessant.',
      },
    ],
  },

  // ── EMPFEHLUNG-TAB (Risiko + Pricing) ──
  recommendation: {
    risk: {
      level: 'Mittel',
      intro: 'Der Case ist grundsätzlich stark, aber nicht risikofrei.',
      mainValidation:
        'Der größte Validierungspunkt ist die Abschlussfähigkeit aus qualifizierten Terminen. Sicherheitstechnik ist oft erklärungsbedürftig, teilweise standortabhängig und kann mehrere Gespräche benötigen.',
      risks: [
        {
          title: 'Sales Cycle kann länger sein als erwartet',
          body: 'Gewerbliche Sicherheitssysteme werden selten spontan gekauft. Oft braucht es Besichtigung, Angebot, technische Einschätzung und interne Abstimmung.',
        },
        {
          title: 'Pain muss konkret genug formuliert werden',
          body: '„Brauchen Sie eine Alarmanlage?“ ist zu schwach. Die Ansprache muss über Risiko, Verantwortung, veraltete Systeme oder konkrete Objektarten laufen.',
        },
        {
          title: 'Zielgruppen müssen sauber priorisiert werden',
          body: 'Nicht jede Branche ist gleich stark. Hausverwaltungen, Lagerbetriebe und Unternehmen mit mehreren Standorten sind vermutlich stärker als kleine Einzelbetriebe.',
        },
      ],
      mitigationTitle: 'Risikominderung',
      mitigation:
        'Die erste Phase sollte nicht auf maximale Skalierung ausgelegt sein. Sie sollte herausfinden, welche Zielgruppe am stärksten reagiert.',
      testSegments: [
        'Hausverwaltungen und Immobilienverwalter',
        'Lager- und Logistikbetriebe',
        'Hotels, Praxen und Kanzleien mit erhöhtem Sicherheitsbedarf',
      ],
    },
    pricing: {
      // packageName, Preis, Termin-Bonus & Detail-Zahlen werden live berechnet
      whyTitle: 'Warum dieses Paket?',
      why: [
        '{tier} ist hier die sinnvollste Empfehlung, weil der Case stark genug für ernsthafte Pipeline-Arbeit ist, aber vor einer Premium-Skalierung zuerst die besten Zielsegmente validiert werden sollten.',
        'Foundation wäre möglich, aber vermutlich zu defensiv. Premium wäre erst dann sinnvoll, wenn klar ist, welche Zielgruppe am stärksten konvertiert und die Terminmenge operativ sauber verarbeitet werden kann.',
      ],
      narrativeTitle: 'Pricing Narrative',
      narrative: [
        'Das Modell passt, weil der Kundenwert hoch genug ist, um eine variable Termin-Komponente wirtschaftlich zu rechtfertigen.',
        'Die fixe monatliche Fee deckt Strategie, Zielgruppenarbeit, Kampagnenaufbau, Testing, Optimierung und laufende Umsetzung ab.',
        'Der Appointment Value greift erst ab dem 4. Termin. Dadurch bleibt das Modell wertbasiert: Je mehr echte Chancen entstehen, desto stärker ist auch der variable Anteil.',
      ],
    },
  },
};

export default sampleReport;
